import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { MAX_BYTES } from "./utils";

/**
 * Attachment CRUD for a given parent (invoice, journal, lead, etc.).
 * Handles list, chunk-free multipart upload with progress, and delete.
 */
export default function useAttachments({ parentType, parentId }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/attachments?parent_type=${parentType}&parent_id=${parentId}`);
      setItems(data || []);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("Attachments load failed", err);
    }
  }, [parentType, parentId]);

  useEffect(() => { refresh(); }, [refresh]);

  const upload = useCallback(async (files) => {
    if (!files || !files.length) return;
    for (const file of files) {
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name}: exceeds 100 MB limit`);
        continue;
      }
      const fd = new FormData();
      fd.append("parent_type", parentType);
      fd.append("parent_id", parentId);
      fd.append("file", file);
      setBusy(true);
      setProgress(0);
      try {
        await api.post("/attachments/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
          },
        });
        toast.success(`${file.name} uploaded`);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || `${file.name} upload failed`);
      } finally {
        setBusy(false);
        setProgress(0);
      }
    }
    refresh();
  }, [parentType, parentId, refresh]);

  const deleteOne = useCallback(async (att) => {
    if (!window.confirm(`Delete ${att.original_filename}?`)) return;
    try {
      await api.delete(`/attachments/${att.id}`);
      toast.success("Deleted");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }, [refresh]);

  return { items, busy, progress, upload, deleteOne, refresh };
}
